import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Empty,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  EyeOutlined,
  PlusOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import dayjs from "dayjs";

import { apiClient } from "../../../services/api/apiClient";

const { Text } = Typography;

// Shape returned by the unified Ticket list endpoint
// (`TicketListSerializer`). We keep the field names short here and
// project them into the audit-incidence display.
interface IncidenceRow {
  id: number;
  code: string;
  subject: string;
  description?: string;
  status: number;
  status_display: string;
  priority: string;
  priority_display: string;
  category_name: string;
  created_by_name: string | null;
  attachment_url: string | null;
  created: string;
}

interface AuditIncidencesSectionProps {
  auditId: number;
  /** Opens the "Crear Incidencia" modal owned by the parent. */
  onCreateIncidence: () => void;
  /** Bump to force a re-fetch (e.g. after the parent's create modal
   *  closes successfully). */
  refreshKey?: number;
  /** Fires with the row count whenever the data loads — used by the
   *  parent to render a tab badge. */
  onCountChange?: (count: number) => void;
}

/**
 * List of incidences linked to the current audit. Lives in the
 * "Incidencias" tab of the audit detail. Each row links to the
 * full incidence detail page.
 */
export const AuditIncidencesSection = ({
  auditId,
  onCreateIncidence,
  refreshKey = 0,
  onCountChange,
}: AuditIncidencesSectionProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState<IncidenceRow[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!auditId) return;
    setLoading(true);
    try {
      // Unified endpoint: every audit incidence is a Ticket with
      // audit=auditId and category.kind="audit".
      const res = await apiClient.get(
        `/v1/tickets/?audit=${auditId}&category__kind=audit&page_size=200`,
      );
      // The unified tickets viewset goes through the Refine-compatible
      // paginator, which returns `{ data: [...], total: N }` — not
      // DRF's default `{ results: [...], count: N }`. Try every
      // shape we know about so a future paginator change doesn't
      // crash the tab again (the previous code assumed `results`,
      // ended up assigning the whole envelope object to `data`, and
      // antd Table blew up on the non-array `dataSource`).
      const body = res.data as any;
      const items: IncidenceRow[] = Array.isArray(body)
        ? body
        : Array.isArray(body?.data)
          ? body.data
          : Array.isArray(body?.results)
            ? body.results
            : [];
      setData(items);
      onCountChange?.(items.length);
    } catch {
      message.error(
        t("incidence.load_error", "Error al cargar las incidencias"),
      );
      setData([]);
      onCountChange?.(0);
    } finally {
      setLoading(false);
    }
  }, [auditId, t, onCountChange]);

  useEffect(() => {
    fetchData();
    // `refreshKey` participates so a parent bump re-fetches.
  }, [fetchData, refreshKey]);

  const statusColor = (s: number) =>
    s === 1 ? "gold" : s === 2 ? "green" : "default";

  const priorityColor = (p: string) =>
    p === "critical"
      ? "red"
      : p === "high"
        ? "volcano"
        : p === "medium"
          ? "blue"
          : "default";

  const columns = [
    {
      title: t("incidence.num_short", "Nº"),
      dataIndex: "code",
      key: "code",
      width: 110,
      render: (v: string) => <Text strong>{v || "—"}</Text>,
    },
    {
      title: t("incidence.subject", "Asunto"),
      dataIndex: "subject",
      key: "subject",
      ellipsis: true,
      render: (subject: string, r: IncidenceRow) => (
        <Tooltip title={subject || r.description?.replace(/<[^>]+>/g, "")}>
          {subject || (
            <Text type="secondary">
              {t("incidence.no_subject", "(sin asunto)")}
            </Text>
          )}
        </Tooltip>
      ),
    },
    {
      title: t("incidence.type_short", "Tipo"),
      dataIndex: "category_name",
      key: "category_name",
      width: 150,
      ellipsis: true,
    },
    {
      // `incidence.priority` in the locale files is a NAMESPACE
      // (it nests `low / medium / high / critical`), so resolving
      // it bare would return an object and antd's Table title
      // would render "[object Object]" / log a warning. Use the
      // dedicated leaf key `incidence.priority_label` instead.
      title: t("incidence.priority_label", "Prioridad"),
      dataIndex: "priority_display",
      key: "priority",
      width: 100,
      render: (label: string, r: IncidenceRow) => (
        <Tag color={priorityColor(r.priority)}>{label || r.priority}</Tag>
      ),
    },
    {
      title: t("incidence.status_short", "Estado"),
      dataIndex: "status_display",
      key: "status",
      width: 100,
      render: (label: string, r: IncidenceRow) => (
        <Tag color={statusColor(r.status)}>
          {label || r.status}
        </Tag>
      ),
    },
    {
      title: t("incidence.reporter_short", "Creada por"),
      dataIndex: "created_by_name",
      key: "created_by_name",
      width: 140,
      ellipsis: true,
      render: (name: string | null) => name || "—",
    },
    {
      title: t("incidence.created", "Fecha"),
      dataIndex: "created",
      key: "created",
      width: 110,
      render: (v: string) => (v ? dayjs(v).format("DD/MM/YYYY") : "—"),
    },
    // "Archivo" column intentionally removed — the attachment is
    // still reachable from the incidence detail page reached via
    // the "Ver" action below.
    {
      title: t("common.actions", "Acciones"),
      key: "actions",
      width: 80,
      fixed: "right" as const,
      render: (_: any, r: IncidenceRow) => (
        // Audit tickets get viewed under the existing /incidences/:id
        // route — that page still works against the unified backend
        // because it reads via the v1/tickets API now.
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/incidences/${r.id}`)}
        >
          {t("common.view_short", "Ver")}
        </Button>
      ),
    },
  ];

  return (
    <Card
      title={
        <Space>
          <WarningOutlined style={{ color: "#faad14" }} />
          {t("audit.incidences_title", "Incidencias de la auditoría")}
        </Space>
      }
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={onCreateIncidence}
        >
          {t("incidence.create", "Crear Incidencia")}
        </Button>
      }
      bordered={false}
      style={{ marginBottom: 0 }}
      loading={loading && data.length === 0}
    >
      {data.length === 0 && !loading ? (
        <Empty
          description={t(
            "audit.no_incidences",
            "No hay incidencias registradas en esta auditoría",
          )}
        />
      ) : (
        <Table<IncidenceRow>
          dataSource={data}
          columns={columns}
          rowKey="id"
          pagination={false}
          // Slightly higher min-width than the column total so antd
          // doesn't squeeze the last cells. "middle" gives a bit of
          // breathing room without bloating the row height.
          scroll={{ x: 920 }}
          size="middle"
          loading={loading}
        />
      )}
    </Card>
  );
};

export default AuditIncidencesSection;
